import { useContext } from 'react'
import { DateTime } from 'luxon'
import type { AppDispatch } from '@redux/store'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload as UseFormBasePayload } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import { setTimeToDayEnd } from '@functions/dateTime'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as taskListSectionActions from '@redux/modules/taskListSection'

import defaultFormState, { requiredFields } from '@models/taskListSection'

import type { TaskListSectionModuleState, EntityOptions } from '@redux/modules/types'
import type { TaskListModel, TaskListSectionModel } from '@models/types'

export const generateUrls = (taskListSection: Partial<TaskListSectionModel> = {}) => {
  const { id, task_list_id } = taskListSection

  return {
    editTaskListSectionUrl: `#/projects/${task_list_id}/tasks?taskListSectionId=${id}`,
  }
}

type EntityActionFunctionParams = {
  entityParams: Partial<TaskListSectionModel>,
  dispatch: AppDispatch,
  requestOptions: EntityOptions,
}

const createTaskListSection = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { createTaskListSection: createFn } = taskListSectionActions

  const updatedParams = {
    ...entityParams,
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams, requestOptions))
}

const updateTaskListSection = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { updateTaskListSection: updateFn } = taskListSectionActions

  const updatedParams = {
    ...entityParams,
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTaskListSectionParams = {
  dispatch: AppDispatch,
  taskListSection: Partial<TaskListSectionModel>,
}

const deleteTaskListSection = (params: DeleteTaskListSectionParams) => {
  const { taskListSection, dispatch } = params
  const { deleteTaskListSection: deleteFn } = taskListSectionActions

  return dispatch(deleteFn(taskListSection))
}

// @ts-expect-error TS7006
const createOrEditTaskListSection = params => new Promise((resolve, reject) => {
  const { taskListSection, showCreateOrEditTaskListSectionModal } = params

  if (showCreateOrEditTaskListSectionModal){
    const payload = {
      taskListSection,
    }

    showCreateOrEditTaskListSectionModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditTaskListSectionModal not defined in PageContext callbacks'))
})

type UseFormPayload = UseFormBasePayload & {
  creating?: boolean,
  updating?: boolean,
}

export function useTaskListSectionForm(
  taskListSection: Partial<TaskListSectionModel>,
  options?: UseFormOptions,
): UseFormPayload {
  const { validateOn } = options || {}

  const { creating, updating } = useSelector(reduxState => reduxState.taskListSections)

  // Map entity so we can set start and end dates
  // @ts-expect-error
  const dueDateString = taskListSection?.dates?.due?.date_time_with_timezone

  const entity = {
    ...taskListSection,
    due_date:
      taskListSection.id && dueDateString
        ? DateTime.fromISO(dueDateString).toFormat('yyyy-MM-dd')
        : taskListSection.due_date,
  }

  const taskListSectionForm = useForm(
    defaultFormState,
    { entity, requiredFields: [...requiredFields], validateOn },
    [taskListSection.id, taskListSection.cache_key],
  )

  return {
    ...taskListSectionForm,
    creating,
    updating,
  }
}

export const useRelations = (entity: Partial<TaskListSectionModel> = {}) => {
  const { task_list_id } = entity

  const { taskLists } = useSelector(reduxState => reduxState.entities)

  const taskList = task_list_id ? taskLists[task_list_id] : {}

  return {
    taskList,
  }
}

interface UseTaskListSectionOptions {
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

type UseTaskListSectionPayload = {
  callbacks: {
    createOrEditTaskListSection: () => void,
    createTaskListSection: (entityParams: Partial<TaskListSectionModel>, requestOptions: EntityOptions) => void,
    deleteTaskListSection: (entityParams: Partial<TaskListSectionModel>, requestOptions: EntityOptions) => void,
    updateTaskListSection: (entityParams: Partial<TaskListSectionModel>, requestOptions: EntityOptions) => void,
  },
  taskList: Partial<TaskListModel>,
  taskListSection: Partial<TaskListSectionModel>,
  creating: boolean,
  loading: boolean,
  updating: boolean,
  urls: ReturnType<typeof generateUrls>,
}

function useTaskListSection(
  initEntity: Partial<TaskListSectionModel> = {},
  options: UseTaskListSectionOptions = {},
): UseTaskListSectionPayload {
  const { performHttpRequests, requestOptions } = options || {}

  // @ts-expect-error TS2345
  const { entity: taskListSection } = useLatestEntity(initEntity, 'taskListSections')
  const { id } = taskListSection

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditTaskListSectionModal } = callbacks || {}

  // @ts-expect-error TS2345
  const { taskList } = useRelations(taskListSection)

  const { creating, loading, updating } = useSelector(reduxState => reduxState.taskListSections)

  useReduxAction(
    'taskListSections',
    'loadTaskListSection',
    {
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, reqOptions: EntityOptions) => action(id, reqOptions),
      // @ts-expect-error
      shouldPerformFn: (entityReducer: TaskListSectionModuleState) => {
        const { loadedIds } = entityReducer
        // @ts-expect-error TS2345
        return performHttpRequests && !loadedIds?.includes(id)
      },
    },
  )

  return {
    callbacks: {
      createOrEditTaskListSection: () => (
        createOrEditTaskListSection({
          showCreateOrEditTaskListSectionModal,
          taskListSection,
        })
      ),
      createTaskListSection: (entityParams: Partial<TaskListSectionModel>, entityRequestOptions: EntityOptions) => (
        createTaskListSection({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      deleteTaskListSection: () => (
        // @ts-expect-error TS2322
        deleteTaskListSection({ taskListSection, dispatch })
      ),
      updateTaskListSection: (entityParams: Partial<TaskListSectionModel>, entityRequestOptions: EntityOptions) => (
        updateTaskListSection({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
    },
    creating,
    loading,
    taskList,
    // @ts-expect-error TS2322
    taskListSection,
    updating,
    // @ts-expect-error TS2345
    urls: generateUrls(taskListSection),
  }
}

export default useTaskListSection
